import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Search, Edit2, Trash2, User, Users, ChevronDown, Eye, X, AlertCircle } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useKeycloak } from '@react-keycloak/web';
import { authenticatedFetch, SESSION_TIMEOUT } from '../utils/auth';
import { API_BASE_URL} from '../utils/config';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../hooks/useLanguage';
import { useTeamPermissions } from '../hooks/useTeamPermissions';
import SectionSelectionModal from './SectionSelectionModal';
import EnhancedPreviewModal from './EnhancedPreviewModal';
import AuthorizationModal from './AuthorizationModal';
import RecoveryOptionsDialog from './RecoveryOptionsDialog';
import TenantContextDisplay from './TenantContextDisplay';
import UserProfileDropdown from './UserProfileDropdown';
import { useAuthCheck } from '../hooks/useAuthCheck';
import { useRecoveryDialog } from '../hooks/useRecoveryDialog';
import { useAdmin } from '../context/AdminContext';
import supervityLogo from '../assets/supervity_favicon.png';
import { debugAuthState } from '../utils/debugAuth';
import Sidebar from './Sidebar';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { keycloak, initialized } = useKeycloak();
  const { t, i18n } = useTranslation();
  const { isOwner, hasWriteAccess } = useTeamPermissions();
  const { checkAuth, showAuthModal, closeAuthModal } = useAuthCheck();
  const { 
    showRecoveryDialog, 
    isCheckingAuth, 
    handleUnauthorizedAccess, 
    closeRecoveryDialog, 
    handleRecoverySuccess 
  } = useRecoveryDialog();

  // Custom recovery success handler to update local state
  const handleRecoverySuccessLocal = () => {
    setIsAuthorized(true);
    handleRecoverySuccess();
  };
  const { isAdmin, integration, hasIntegration, availableTenants, serviceConnections, isLoading: isAdminLoading } = useAdmin();
  // From GET /api/me/full: backend sets is_configured when workflow_imported, account_configured,
  // and workflow_saved are all true. Do not AND with agent_ids here — those flags can lag OAuth
  // and would falsely show "integration not connected" after a valid setup.
  // While AdminContext is still loading, treat status as unknown (null) so we don't false-trigger warnings.
  //
  // Separately, `serviceConnections` (from GET /api/me/full's `service_connections`) reflects the
  // live "Service Connections" (Box/Snowflake/Salesforce) state directly from ServiceCredential —
  // independent of whether an Integration row exists yet. Admins who connect services via OAuth
  // before ever completing the legacy Integration setup wizard would otherwise never have
  // `integration.agent_ids_configured` populated at all (integration stays null), making the
  // "ready to create a report" check impossible to satisfy no matter how many services were
  // connected. `integration?.agent_ids_configured` is kept as a fallback/merge for the legacy
  // manual Agent-ID/Skill-ID setup path.
  // The "ready to create a report" check is intentionally strict: Box and Salesforce must both be
  // connected. If either one is disconnected, the warning must reappear — so we do NOT fall back
  // to "at least one connected" (`.some`) or to the legacy workflow_imported/account_configured/
  // workflow_saved checkboxes here, since those can stay true even after a service is
  // disconnected and would mask a missing connection.
  // Snowflake is optional — it is intentionally excluded from this check regardless of whether
  // it is connected or not.
  const agentIdsConfigured = {
    ...(integration?.agent_ids_configured ?? {}),
    ...(serviceConnections ?? {}),
  };
  const allServicesConnected = ['box', 'salesforce'].every(
    (key) => !!agentIdsConfigured[key]
  );
  const integrationStatus = isAdminLoading
    ? null
    : {
        ...(integration ?? {}),
        is_configured: !!integration?.is_configured,
        all_services_connected: allServicesConnected,
        is_agent_ready: allServicesConnected,
      };
  const tenantAdmins = availableTenants ?? [];

  
  // Global language — single source of truth shared across all pages
  const [currentLanguage, setLanguage] = useLanguage();

  // Email filter state
  const [emailFilters, setEmailFilters] = useState([]);
  const [selectedEmailFilter, setSelectedEmailFilter] = useState(null);
  const [showEmailFilter, setShowEmailFilter] = useState(false);
  const handleLanguageChange = async (newLanguage) => {
    setLanguage(newLanguage);
    // Refresh dashboard list so any language-sensitive labels update
    await fetchDashboards();
  };
  
  const [dashboards, setDashboards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showIntegrationWarning, setShowIntegrationWarning] = useState(false);
  const [pendingDashboardId, setPendingDashboardId] = useState(null);
  const [newDashboardTitle, setNewDashboardTitle] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(true);
  
  // Function to check initial authorization status
  const checkInitialAuthorization = async () => {
    const authorized = await handleUnauthorizedAccess(false);
    setIsAuthorized(authorized);
    if (!authorized) {
      setTimeout(() => { handleUnauthorizedAccess(true); }, 1000);
    }
  };

  // Function to handle logout
  const handleLogout = () => {
    if (keycloak && typeof keycloak.logout === 'function') {
      keycloak.logout({
        redirectUri: window.location.origin
      });
    } else {
      console.error('Keycloak not properly initialized');
      // Redirect to Keycloak login if not initialized
      const baseUrl = import.meta.env.VITE_KEYCLOAK_URL;
    const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
    window.location.href = `${cleanBaseUrl}/auth/realms/techforce/protocol/openid-connect/auth`;
    }
  };
  
  // Fetch dashboards on component mount
  // Integration status and tenant admins now come from AdminContext (GET /api/me)
  useEffect(() => {
    if (initialized && keycloak.authenticated) {
      fetchDashboards();
      checkInitialAuthorization();
    } else if (initialized && !keycloak.authenticated) {
      keycloak.login({
        idpHint: 'default',
        redirectUri: window.location.origin + `/dashboards`,
        maxAge: SESSION_TIMEOUT
      });
    }
  }, [initialized, keycloak.authenticated]);
  
  // Function to fetch dashboards
  const fetchDashboards = async () => {
    setIsLoading(true);
    try {
      let url = `/api/dashboards/get/all`;
      
      const response = await authenticatedFetch(
        url,
        {},
        keycloak.token
      );
      
      // Parse the response JSON
      const data = await response.json();
      
      // Map backend field names to frontend expected names
      console.log("owner_email", keycloak.tokenParsed?.email, data)
      if (data.owner_email === null) {
        data.owner_email = keycloak.tokenParsed?.email;
      }
      const mappedDashboards = data.map(dashboard => {
        return {
          id: dashboard.id,
          title: dashboard.name || 'Untitled Dashboard',
          description: dashboard.company_name || '',
          created_at: dashboard.created_at,
          team_id: dashboard.team_id,
          is_shared: !!dashboard.team_id,
          author_name: dashboard.owner_email || keycloak.tokenParsed?.email,
          owner_email: dashboard.owner_email // Preserve the original owner email
        };
      });
      
      setDashboards(mappedDashboards);
      
      // Extract unique emails for filtering
      const uniqueEmails = [...new Set(mappedDashboards
        .map(dashboard => dashboard.owner_email)
        .filter(email => email)
      )];
      
      // Add current user's email if not already in the list
      if (keycloak.tokenParsed?.email && !uniqueEmails.includes(keycloak.tokenParsed.email)) {
        uniqueEmails.push(keycloak.tokenParsed.email);
      }
      
      // Add "All" option at the beginning
      setEmailFilters([null, ...uniqueEmails]);
    } catch (error) {
      console.error('Error fetching dashboards:', error);
      toast.error(t('common.error'));
      setDashboards([]); // Set empty array on error
      
      // Always add current user's email even on error
      setEmailFilters([null, keycloak.tokenParsed?.email].filter(Boolean));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Actually perform dashboard creation (called directly or after warning is dismissed)
  const doCreateDashboard = async () => {
    setShowIntegrationWarning(false);
    setShowCreateModal(false);
    setIsCreating(true);
    try {
      const requestBody = { title: newDashboardTitle };
      console.log('Creating dashboard with data:', requestBody);

      const response = await authenticatedFetch(
        '/api/dashboards/create',
        { method: 'POST', body: JSON.stringify(requestBody) },
        keycloak.token
      );

      const data = await response.json();
      console.log('Dashboard created successfully:', data);

      const dashboardWithOwner = {
        id: data.id,
        title: data.name,
        created_at: data.created_at,
        team_id: data.team_id,
        is_shared: !!data.team_id,
        author_name: keycloak.tokenParsed?.email,
        owner_email: keycloak.tokenParsed?.email,
      };

      setDashboards(prev => [...prev, dashboardWithOwner]);
      toast.success(t('common.success'));
      setShowCreateModal(false);
      setNewDashboardTitle('');
    } catch (error) {
      console.error('Error creating dashboard:', error);
      toast.error(`${t('common.error')}: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  // Function to create a new dashboard
  const createDashboard = async () => {
    try {
      if (!newDashboardTitle.trim()) {
        toast.error(t('dashboardModal.dashboardTitle') + t('common.error'));
        return;
      }

      // Only warn once AdminContext has finished loading.
      // Strict check for both admins and regular users: Box and Salesforce must both be
      // connected (Snowflake is optional and does not affect this check). If either required
      // service is disconnected, the warning must reappear — so this intentionally does NOT
      // fall back to "at least one connected" or to the legacy workflow_imported/
      // account_configured/workflow_saved checkboxes.
      const integrationNotReady = !integrationStatus?.all_services_connected;

      if (!isAdminLoading && integrationNotReady) {
        setShowCreateModal(false);
        setShowIntegrationWarning(true);
        return;
      }

      await doCreateDashboard();
    } catch (error) {
      console.error('Error creating dashboard:', error);
      toast.error(`${t('common.error')}: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };
  
  // Function to delete a dashboard
  const deleteDashboard = async (id) => {
    // Get the dashboard by ID
    const dashboard = dashboards.find(d => d.id === id);
    
    if (!dashboard) {
      toast.error(t('dashboard.notFound'));
      return;
    }
    
    // Check if user has permission to delete:
    // User must be the owner OR have write access (if it's a team dashboard)
    const isCurrentUserOwner = dashboard.owner_email === keycloak.tokenParsed?.email;
    const hasPermissionToDelete = isCurrentUserOwner || hasWriteAccess(dashboard);
    
    console.log('Delete permission check:', {
      dashboardId: dashboard.id,
      dashboardTitle: dashboard.title,
      dashboardOwnerEmail: dashboard.owner_email,
      currentUserEmail: keycloak.tokenParsed?.email,
      isCurrentUserOwner,
      hasWriteAccess: hasWriteAccess(dashboard),
      hasPermissionToDelete
    });
    
    if (!hasPermissionToDelete) {
      toast.error(t('dashboard.noDeletePermission'));
      return;
    }
    
    if (!window.confirm(t('dashboard.confirmDelete'))) {
      return;
    }
    
    try {
      setIsDeleting(true);
      const response = await authenticatedFetch(
        `/api/dashboards/delete/${id}`,
        {
          method: 'DELETE'
        },
        keycloak.token
      );
      
      // Check if the response is ok
      if (!response.ok) {
        throw new Error(`Failed to delete dashboard: ${response.status}`);
      }
      
      // Update the dashboards list
      setDashboards(prev => prev.filter(dashboard => dashboard.id !== id));
      toast.success(t('common.success'));
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      toast.error(t('common.error'));
      // Optionally refresh the dashboards list to ensure consistency
      fetchDashboards();
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Function to navigate to dashboard detail — no warning here, shown only on Create Report
  const goToDashboard = async (dashboardId) => {
    navigate(`/dashboard/${dashboardId}`);
  };
  
  // Filter dashboards based on search term and email filter
  const filteredDashboards = dashboards.filter(dashboard => {
    // Text search filter
    const matchesSearch = (
      (dashboard.title?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (dashboard.description && dashboard.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    // Email filter
    const matchesEmail = !selectedEmailFilter || dashboard.owner_email === selectedEmailFilter;
    
    return matchesSearch && matchesEmail;
  });
  
  // Handle email filter selection
  const handleEmailFilterSelect = (email) => {
    setSelectedEmailFilter(email);
    setShowEmailFilter(false);
  };

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLanguage, setPreviewLanguage] = useState('en');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [showSectionSelectionModal, setShowSectionSelectionModal] = useState(false);
  const [sectionSelectionDashboard, setSectionSelectionDashboard] = useState(null);
  const [sectionSelectionLanguage, setSectionSelectionLanguage] = useState('en');
  const [pendingPreview, setPendingPreview] = useState(null);
  
  // Enhanced preview modal state
  const [showEnhancedPreviewModal, setShowEnhancedPreviewModal] = useState(false);
  const [enhancedPreviewDashboard, setEnhancedPreviewDashboard] = useState(null);

  // Function to generate PDF preview
  const generatePreview = async (dashboardId, language, selectedSections = null) => {
    setIsGeneratingPreview(true);
    
    console.log('[generatePreview] Starting with params:', {
      dashboardId,
      language,
      timestamp: new Date().toISOString()
    });
    
    try {
      // Get the dashboard details to get the company name
      const dashboard = dashboards.find(d => d.id === dashboardId);
      if (!dashboard) {
        throw new Error('Dashboard not found');
      }

      console.log('[generatePreview] Dashboard found:', dashboard.title);

      // First, check if there's data available by fetching sections
      const sectionsEndpoint = language === 'ja' 
        ? `api/dashboards/${dashboardId}/sections/japanese`
        : `api/dashboards/${dashboardId}/sections`;
      
      console.log('[generatePreview] Checking sections with endpoint:', sectionsEndpoint);
      
      const sectionsResponse = await authenticatedFetch(
        sectionsEndpoint,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        },
        keycloak.token
      );

      console.log('[generatePreview] Sections response status:', sectionsResponse.status, sectionsResponse.statusText);

      if (!sectionsResponse.ok) {
        console.error('[generatePreview] Failed to fetch sections:', sectionsResponse.status, sectionsResponse.statusText);
        throw new Error('Failed to fetch dashboard sections');
      }

      const sectionsData = await sectionsResponse.json();
      console.log('[generatePreview] Sections data keys:', Object.keys(sectionsData));
      
      // Check if there's any valid content in the sections
      const hasValidContent = Object.values(sectionsData).some(content => 
        content && content !== '{}' && content.trim() !== ''
      );

      console.log('[generatePreview] Has valid content:', hasValidContent);

      // If no data available, don't attempt PDF generation and don't show error toast
      if (!hasValidContent) {
        console.log('[generatePreview] No data available for preview, skipping PDF generation');
        
        // Check if this is a request for Japanese but no Japanese data exists
        if (language === 'ja') {
          console.log('[generatePreview] No Japanese data found, checking if English data exists...');
          
          try {
            const englishSectionsResponse = await authenticatedFetch(
              `api/dashboards/${dashboardId}/sections`,
              {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json'
                }
              },
              keycloak.token
            );
            
            if (englishSectionsResponse.ok) {
              const englishSectionsData = await englishSectionsResponse.json();
              const hasEnglishContent = Object.values(englishSectionsData).some(content => 
                content && content !== '{}' && content.trim() !== ''
              );
              
              if (hasEnglishContent) {
                console.log('[generatePreview] English data exists, suggesting research generation needed in Japanese');
                toast.error(t('researchDashboard.aiMessages.noJapaneseDataAvailable') || 'No Japanese data available. Please generate research in Japanese first.');
              } else {
                console.log('[generatePreview] No data in either language');
              }
            }
          } catch (englishCheckError) {
            console.log('[generatePreview] Error checking English sections:', englishCheckError);
          }
        }
        
        setIsGeneratingPreview(false);
        return;
      }

      // If data exists, proceed with PDF generation
      const company_name = localStorage.getItem(`dashboard_${dashboardId}_selectedCompany`) || dashboard.title;
      
      const pdfEndpoint = `api/dashboards/${dashboardId}/generate-pdf/${language}`;
      console.log('[generatePreview] PDF generation endpoint:', pdfEndpoint);
      console.log('[generatePreview] Request body:', {
        company_name: company_name,
        platform_language: language
      });

      // Get available sections for this dashboard and language
      let sectionsWithContent;
      if (!selectedSections) {
        try {
          const availableSectionsResponse = await authenticatedFetch(
            `api/dashboards/${dashboardId}/available-sections/${language}`,
            { method: 'GET' },
            keycloak.token
          );

          if (!availableSectionsResponse.ok) {
            // If the new API is not available, fall back to the old sections endpoint
            if (availableSectionsResponse.status === 404) {
              console.log('Available sections API not found, falling back to sections endpoint');
              const fallbackResponse = await authenticatedFetch(
                `api/dashboards/${dashboardId}/sections${language === 'ja' ? '/japanese' : ''}`,
                { method: 'GET' },
                keycloak.token
              );
              
              if (!fallbackResponse.ok) {
                throw new Error('Failed to fetch sections');
              }
              
              const fallbackData = await fallbackResponse.json();
              
              // Get sections that have content (old format)
              sectionsWithContent = Object.entries(fallbackData)
                .filter(([key, content]) => content && content !== '{}' && content.trim() !== '')
                .map(([key]) => key);
            } else {
              throw new Error('Failed to fetch available sections');
            }
          } else {
            const responseData = await availableSectionsResponse.json();
            
            // Handle the new response format from the backend
            if (responseData.available_sections && Array.isArray(responseData.available_sections)) {
              // New format: { available_sections: [{ key, title, has_content }] }
              sectionsWithContent = responseData.available_sections
                .filter(section => section.has_content)
                .map(section => section.key);
            } else {
              // Fallback to old format: { key: content }
              sectionsWithContent = Object.entries(responseData)
                .filter(([key, content]) => content && content !== '{}' && content.trim() !== '')
                .map(([key]) => key);
            }
          }
        } catch (error) {
          console.error('Error fetching sections:', error);
          throw new Error('Failed to fetch sections');
        }
      } else {
        sectionsWithContent = selectedSections;
      }

      if (sectionsWithContent.length === 0) {
        throw new Error('No sections with content available');
      }

      const response = await authenticatedFetch(
        pdfEndpoint,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/pdf',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            company_name: company_name,
            selected_sections: sectionsWithContent,
            platform_language: language
          })
        },
        keycloak.token
      );

      console.log('[generatePreview] PDF generation response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[generatePreview] PDF generation failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText
        });
        throw new Error('Failed to generate preview');
      }

      const blob = await response.blob();
      console.log('[generatePreview] PDF blob created, size:', blob.size);
      
      const url = window.URL.createObjectURL(blob);
      console.log('[generatePreview] PDF URL created:', url);
      
      setPreviewUrl(url);
    } catch (error) {
      console.error('[generatePreview] Error:', error);
      // Only show error toast for actual API errors, not for missing data
      if (error.message !== 'No data available') {
        toast.error(t('researchDashboard.aiMessages.pdfError'));
      }
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // Function to handle preview button click
  const handlePreviewClick = (dashboard, language = 'en') => {
    setEnhancedPreviewDashboard(dashboard);
    setShowEnhancedPreviewModal(true);
  };

  // Function to switch preview language
  const handleLanguageSwitch = async (language) => {
    console.log('[handleLanguageSwitch] Switching to language:', language, 'for dashboard:', selectedDashboard?.id);
    
    setPreviewLanguage(language);
    setPreviewUrl(null);
    
    console.log('[handleLanguageSwitch] Starting preview generation...');
    await generatePreview(selectedDashboard.id, language);
    console.log('[handleLanguageSwitch] Preview generation completed');
  };

  // Function to handle section selection confirmation
  const handleSectionSelectionConfirm = async (selectedSections) => {
    setShowSectionSelectionModal(false);
    if (!sectionSelectionDashboard) return;
    setSelectedDashboard(sectionSelectionDashboard);
    setShowPreviewModal(true);
    setPreviewLanguage(sectionSelectionLanguage);
    setPreviewUrl(null);
    await generatePreview(sectionSelectionDashboard.id, sectionSelectionLanguage, selectedSections);
  };

  // Cleanup preview URL when modal closes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Left Sidebar Navigation */}
      <Sidebar />

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
      <Toaster position="top-right" />
      
      {isLoading ? (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
      ) : (
        <div className="min-h-screen bg-gray-50 p-6 flex flex-col">
          {/* Authorization banner removed - all authenticated users now have access */}
          
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 mb-0">{t('dashboard.title')}</h1>
                  <p className="text-sm text-gray-500">{t('dashboard.subtitle') || 'Your research workspaces'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button 
                  onClick={() => setShowCreateModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition-colors shadow-sm text-sm font-medium"
                  id="dashboard-create-btn"
                >
                  <Plus size={16} className="mr-2" />
                  {t('dashboard.createButton')}
                </button>
                
                {/* Language Switcher */}
                <LanguageSwitcher 
                  currentLanguage={currentLanguage}
                  onLanguageChange={handleLanguageChange}
                  isResearchDashboard={false}
                  disabled={isLoading}
                />
                
                {/* User Profile Dropdown */}
                <UserProfileDropdown />
              </div>
            </div>
          </div>
          
          {/* Integration Setup Dialog - Unified approach for both scenarios */}
          {(() => {
            const hasExistingAdmins = tenantAdmins && tenantAdmins.length > 0;
            const isNotConfigured = integrationStatus?.is_configured !== true;
            
            // Unified logic: Show RecoveryDialog for all integration setup scenarios
            // This provides a consistent experience regardless of admin existence
            const shouldShowSetupDialog = (showRecoveryDialog || isNotConfigured) && !integrationStatus?.is_configured;
            
            console.log('📊 DashboardPage: Unified Dialog Decision:', {
              integrationStatus,
              isConfigured: integrationStatus?.is_configured,
              isConfiguredType: typeof integrationStatus?.is_configured,
              isNotConfigured,
              tenantAdmins: tenantAdmins?.length || 0,
              hasExistingAdmins,
              showRecoveryDialog,
              shouldShowSetupDialog,
              dialogChoice: shouldShowSetupDialog ? 'RecoveryDialog (Unified)' : 'None'
            });
            return null;
          })()}

          {/* Search bar */}
          <div className="flex items-center mb-6">
            <div className="relative flex-grow mr-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('dashboard.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* Email filter dropdown */}
            <div className="relative">
              <button 
                className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50"
                onClick={() => setShowEmailFilter(prev => !prev)}
              >
                {!selectedEmailFilter ? (
                  <span>{t('dashboard.allEmails')}</span>
                ) : (
                  <div className="flex items-center">
                    <span className="truncate max-w-[100px]">
                      {selectedEmailFilter.split('@')[0]}
                    </span>
                    {selectedEmailFilter === keycloak.tokenParsed?.email && (
                      <span className="ml-1 px-1 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                        {t('dashboard.you')}
                      </span>
                    )}
                  </div>
                )}
                <ChevronDown size={14} className="ml-2" />
              </button>
              
              {showEmailFilter && emailFilters.length > 0 && (
                <div className="absolute right-0 z-10 mt-1 w-64 max-h-60 overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" style={{ maxWidth: 'calc(100vw - 20px)' }}>
                  <div className="py-1">
                    {emailFilters.map((email, index) => (
                      <button
                        key={email || 'all'}
                        className={`${
                          selectedEmailFilter === email 
                            ? 'bg-gray-100 text-gray-900' 
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        } block w-full px-4 py-2 text-left text-sm truncate`}
                        onClick={() => handleEmailFilterSelect(email)}
                      >
                        {!email ? t('dashboard.allEmails') : (
                          <div className="flex items-center justify-between">
                            <span className="truncate max-w-[160px] inline-block">
                              {email.split('@')[0]}
                            </span>
                            {email === keycloak.tokenParsed?.email && (
                              <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs whitespace-nowrap flex-shrink-0">
                                {t('dashboard.you')}
                              </span>
                            )}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Scrollable Dashboards list */}
          <div className="overflow-auto flex-1" style={{ height: 'calc(100vh - 220px)' }}>
            <div className="space-y-4">
              {filteredDashboards.length > 0 ? (
                filteredDashboards.map(dashboard => (
                  <div
                    key={dashboard.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                  >
                    <div className="p-4">
                      <div className="flex justify-between">
                        <div>
                          <h3 className="text-lg font-medium text-gray-800 mb-2">{dashboard.title}</h3>
                          <div className="flex items-center text-xs text-gray-500">
                            <User size={14} className="mr-1" />
                            <span>{dashboard.author_name}</span>
                            {dashboard.owner_email === keycloak.tokenParsed?.email && (
                              <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                                {t('dashboard.you')}
                              </span>
                            )}
                            <span className="mx-2">•</span>
                            <span>{new Date(dashboard.created_at).toLocaleDateString()}</span>
                            {dashboard.is_shared && (
                              <>
                                <span className="mx-2">•</span>
                                <span className="flex items-center">
                                  <Users size={14} className="mr-1" />
                                  {t('dashboard.shared')}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <button
                            onClick={() => goToDashboard(dashboard.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                          >
                            <span>{t('common.view')}</span>
                            <FileText size={14} className="ml-1" />
                          </button>
                          
                          {/* Add debug logs */}
                          {console.log('Dashboard Permission Debug:', {
                            dashboardId: dashboard.id,
                            dashboardTitle: dashboard.title,
                            dashboardOwnerEmail: dashboard.owner_email,
                            currentUserEmail: keycloak.tokenParsed?.email,
                            isCurrentUserOwner: dashboard.owner_email === keycloak.tokenParsed?.email,
                            hasWriteAccessCheck: hasWriteAccess(dashboard)
                          })}
                          
                          {/* Show delete button ONLY if user is the owner OR has write access */}
                          {(dashboard.owner_email === keycloak.tokenParsed?.email || hasWriteAccess(dashboard)) && (
                            <button 
                              onClick={() => deleteDashboard(dashboard.id)}
                              disabled={isDeleting}
                              className="text-gray-500 hover:text-red-600 transition-colors"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                          
                          {/* Show read-only indicator if user is not owner and has read-only access */}
                          {dashboard.team_id && dashboard.owner_email !== keycloak.tokenParsed?.email && !hasWriteAccess(dashboard) && (
                            <span className="text-xs text-gray-500 italic flex items-center">
                              <Eye size={14} className="mr-1" />
                              {t('dashboard.readOnly')}
                            </span>
                          )}

                          {/* Add Preview button in the dashboard list */}
                          <button
                            onClick={() => handlePreviewClick(dashboard, 'en')}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                          >
                            <Eye size={14} className="mr-1" />
                            {t('common.preview')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-800 mb-1">
                    {t('dashboard.noDashboards')}
                  </h3>
                  <p className="text-gray-500">
                    {searchTerm 
                      ? t('dashboard.tryDifferentSearch') 
                      : t('dashboard.createFirstDashboard')}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Create dashboard modal */}
          {showCreateModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
                <h2 className="text-xl font-bold mb-4 text-gray-800">{t('dashboardModal.title')}</h2>
                
                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    {t('dashboardModal.dashboardTitle')} <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text"
                    value={newDashboardTitle}
                    onChange={(e) => setNewDashboardTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    placeholder={t('dashboardModal.dashboardTitle')}
                    disabled={isCreating}
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button 
                    onClick={() => {
                      setShowCreateModal(false);
                      setNewDashboardTitle('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
                    disabled={isCreating}
                  >
                    {t('common.cancel')}
                  </button>
                  <button 
                    onClick={createDashboard}
                    disabled={isCreating || !newDashboardTitle.trim()}
                    className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center transition-colors shadow-sm ${
                      isCreating ? 'opacity-75 cursor-not-allowed' : ''
                    }`}
                  >
                    {isCreating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        {t('dashboardModal.creating')}
                      </>
                    ) : (
                      t('common.create')
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Integration Warning Dialog — shown when user tries to create without integration */}
          {showIntegrationWarning && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-[100]">
              <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mr-3">
                    <AlertCircle size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">{t('integrations.notConnectedDialog.title')}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {t('integrations.notConnectedDialog.message')}
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 text-sm text-amber-800">
                  {t('integrations.notConnectedDialog.detail')}
                </div>

                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => {
                      setShowIntegrationWarning(false);
                      setShowCreateModal(false);
                      setPendingDashboardId(null);
                      setNewDashboardTitle('');
                      navigate('/integrations');
                    }}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                  >
                    {t('integrations.notConnectedDialog.connectButton')}
                  </button>
                  <button
                    onClick={() => {
                      setShowIntegrationWarning(false);
                      setPendingDashboardId(null);
                      doCreateDashboard();
                    }}
                    disabled={isCreating}
                    className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    {isCreating ? (
                      <span className="flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                        {t('integrations.notConnectedDialog.creating')}
                      </span>
                    ) : (
                      t('integrations.notConnectedDialog.proceedButton')
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowIntegrationWarning(false);
                      setPendingDashboardId(null);
                      setShowCreateModal(true);
                    }}
                    className="w-full px-4 py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
                  >
                    {t('integrations.notConnectedDialog.cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Preview Modal */}
          {showPreviewModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
              <div className="bg-white rounded-lg w-full max-w-6xl h-[90vh] flex flex-col">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <h2 className="text-xl font-bold text-gray-800">
                      {selectedDashboard?.title} - {t('common.preview')}
                    </h2>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleLanguageSwitch('en')}
                        className={`px-3 py-1 rounded ${
                          previewLanguage === 'en'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        English
                      </button>
                      <button
                        onClick={() => handleLanguageSwitch('ja')}
                        className={`px-3 py-1 rounded ${
                          previewLanguage === 'ja'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        日本語
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowPreviewModal(false);
                      if (previewUrl) {
                        window.URL.revokeObjectURL(previewUrl);
                        setPreviewUrl(null);
                      }
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={24} />
                  </button>
                </div>
                <div className="flex-1 p-4 overflow-hidden">
                  {isGeneratingPreview ? (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                        <p className="text-gray-600">{t('researchDashboard.aiMessages.generatingPdf')}</p>
                      </div>
                    </div>
                  ) : previewUrl ? (
                    <iframe
                      src={previewUrl}
                      className="w-full h-full border-0"
                      title="PDF Preview"
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center p-8">
                        <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-800 mb-2">
                          {t('researchDashboard.aiMessages.noDataAvailable')}
                        </h3>
                        <p className="text-gray-500 mb-4">
                          {t('researchDashboard.aiMessages.generateReportFirst')}
                        </p>
                        <button
                          onClick={() => {
                            setShowPreviewModal(false);
                            goToDashboard(selectedDashboard.id);
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center mx-auto"
                        >
                          <Edit2 size={16} className="mr-2" />
                          {t('common.goToDashboard')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Preview Modal */}
          <EnhancedPreviewModal
            isOpen={showEnhancedPreviewModal}
            onClose={() => setShowEnhancedPreviewModal(false)}
            dashboard={enhancedPreviewDashboard}
          />

          {/* Authorization Modal */}
          <AuthorizationModal
            isOpen={showAuthModal}
            onClose={closeAuthModal}
          />

          {/* Integration Setup Dialog — only shown when explicitly triggered (Connect Integration button) */}
          <RecoveryOptionsDialog
            isOpen={showRecoveryDialog}
            onClose={closeRecoveryDialog}
            onSuccess={handleRecoverySuccessLocal}
            hasExistingAdmins={tenantAdmins && tenantAdmins.length > 0}
            integrationStatus={integrationStatus}
          />
        </div>
      )}
      </div>
    </div>
  );
};

export default DashboardPage; 